import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Button } from '@/components/ui-kit/button';
import { useToast } from '@/hooks/use-toast';
import { useDeleteHotWheel } from '../../hooks/use-hotwheels';
import { HotWheel } from '../../types/hotwheels.types';

interface DeleteHotwheelDialogProps {
  car: HotWheel | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DeleteHotwheelDialog({ car, open, onOpenChange }: DeleteHotwheelDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { mutateAsync: deleteCar, isPending } = useDeleteHotWheel();

  if (!car) return null;

  const handleDelete = async () => {
    try {
      await deleteCar({ itemId: car.ItemId });
      onOpenChange(false);
    } catch (err) {
      toast({
        title: t('DELETE_FAILED'),
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
        <DialogHeader>
          <DialogTitle className="text-white">{t('DELETE_CONFIRM_TITLE')}</DialogTitle>
          <DialogDescription className="text-zinc-400">
            {t('DELETE_CONFIRM_DESCRIPTION', {
              name: `${car.Brand} ${car.ModelNumber}`,
            })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            {t('CANCEL')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isPending}
            className="bg-[#2563EB] hover:bg-blue-600"
          >
            {isPending ? t('LOADING') : t('DELETE_CAR')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}